import { findLastIndex, isNil, range } from 'lodash';
import moment from 'moment';
import { CSSProperties, RefObject, useContext, useEffect, useMemo, useRef } from 'react';
import { View as RNView, StyleSheet } from 'react-native';
import { Text } from '@shared/components/Text';
import { ScrollView } from '@shared/components/View';
import { MBHover } from '@shared/components/hover';
import { MBIconV2 } from '@shared/components/image';
import { MBTouchableOpacity } from '@shared/components/touchable';
import { ColorThemeContext, Colors, SharedStyles, grid } from '@shared/styles';
import { ISOString } from '@shared/types/dates';
import { formatTime } from '@shared/util/date';

const MINUTE_INCREMENT = 15;
const delta = 1000 * 60 * MINUTE_INCREMENT;
const numOptions = (1000 * 60 * 60 * 24) / delta;
const options = range(numOptions).map(i => i * delta);
const itemHeight = grid(4);

interface Props {
  value: string;
  style?: CSSProperties;
  onChange(dateString: ISOString): void;
  /*
        Optional close handler that will render a top bar with a close "X" icon when provided.
    */
  onClose?(): void;
  showClose?: boolean;
}

function useScrollCurrentIndexIntoView(currentIndex: number): RefObject<HTMLDivElement> {
  const scrollView = useRef<HTMLDivElement>(null);
  const hasScrolledIntoView = useRef(false);

  useEffect(() => {
    if (hasScrolledIntoView.current) {
      return;
    }
    hasScrolledIntoView.current = true;
    const topPadding = itemHeight * 2;
    const itemYStart = currentIndex * itemHeight;
    scrollView.current?.scrollTo({ top: itemYStart - topPadding, behavior: 'smooth' });
  }, [currentIndex]);

  return scrollView;
}

export function MBTimePicker({ value, style, onChange, onClose, showClose }: Props) {
  const colors = useContext(ColorThemeContext);
  const currentScrollIndex = useMemo(() => {
    return findLastIndex(options, offset =>
      moment(value).startOf('day').add(offset, 'milliseconds').isSameOrBefore(value),
    );
  }, [value]);

  const scrollView = useScrollCurrentIndexIntoView(currentScrollIndex);

  const dates = useMemo(() => {
    const dateArray = options.map(offset =>
      moment(value || undefined)
        .startOf('day')
        .add(offset, 'milliseconds'),
    );
    if (moment(value).isValid() && !dateArray.some(date => date.isSame(moment(value), 'minute'))) {
      dateArray.push(moment(value));
    }
    return dateArray.sort((a, b) => a.diff(b));
  }, [value]);

  return (
    <RNView style={styles.container}>
      {showClose && !isNil(onClose) && (
        <RNView style={styles.bar}>
          <MBTouchableOpacity onPress={onClose}>
            <MBIconV2 name="exit-heavy" color={colors.foregroundInactive} size={grid(2.5)} />
          </MBTouchableOpacity>
        </RNView>
      )}
      <ScrollView style={style} ref={scrollView}>
        {dates.map((date, index) => (
          <MBTouchableOpacity key={index} onPress={() => onChange(date.toISOString())}>
            <MBHover
              center
              hoverStyle={{ backgroundColor: colors.input.backgroundInactive }}
              style={[
                SharedStyles.borderRadius,
                { height: itemHeight },
                date.isSame(value, 'minute') && { backgroundColor: colors.input.backgroundActive },
              ]}
            >
              <Text size="body1" align="center">
                {formatTime(date.toISOString(), false)}
              </Text>
            </MBHover>
          </MBTouchableOpacity>
        ))}
      </ScrollView>
    </RNView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    marginTop: grid(1),
    borderColor: Colors.v2.gray05,
    borderWidth: 1,
    borderStyle: 'solid',
    borderRadius: grid(1),
    paddingHorizontal: grid(0.5),
  },
  bar: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    paddingTop: grid(1),
    paddingHorizontal: grid(0.75),
  },
});
